/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * TimeTextField
 *
 * @file TimeTextField.jsx
 * @author Satyaki Bose
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Typography } from '@material-ui/core';
import Utils from '../../common/utils';
import { TIMEFIELD_CONSTANTS } from '../../common/constants';
import { useTimeInput } from '../../hooks/useTimeInput';
import AetTimeFieldStyles from './Styles/AetTimeFieldStyles';

const {
  MAX_INPUT_LENGTH,
  FIELD_TYPES
} = TIMEFIELD_CONSTANTS;

const TimeTextField = ({ label, value, onChange, parentApp, icons }) => {
  const classes = Utils.getSyllabusStyles(
    parentApp,
    { ereader: null, aet: AetTimeFieldStyles },
    () => ({})
  );

  const {
    activeField,
    displayHour,
    displayMinute,
    displayPeriod,
    hourRef,
    minuteRef,
    periodRef,
    handleHourChange,
    handleMinuteChange,
    handlePeriodClick,
    handleHourFocus,
    handleMinuteFocus,
    handlePeriodFocus,
    handleHourBlur,
    handleMinuteBlur,
    handlePeriodBlur,
    handleKeyDown
  } = useTimeInput({ value, onChange, periodButtonClassName: classes.periodButton });

  return (
    <div className={classes.timeWrapper}>
      {label && <Typography className={classes.label}>{label}</Typography>}
      <div className={classes.timeFieldWrapper}>
        <div className={classes.segmentedInput}>
          <input
            ref={hourRef}
            type="text"
            value={displayHour}
            onChange={handleHourChange}
            onFocus={handleHourFocus}
            onBlur={handleHourBlur}
            onKeyDown={(e) => handleKeyDown(e, FIELD_TYPES.HOUR)}
            className={`${classes.timeSegment} ${activeField === FIELD_TYPES.HOUR ? classes.activeSegment : ''}`}
            maxLength={MAX_INPUT_LENGTH}
            inputMode="numeric"
            aria-label="Hour"
          />
          <span className={classes.separator}>:</span>
          <input
            ref={minuteRef}
            type="text"
            value={displayMinute}
            onChange={handleMinuteChange}
            onFocus={handleMinuteFocus}
            onBlur={handleMinuteBlur}
            onKeyDown={(e) => handleKeyDown(e, FIELD_TYPES.MINUTE)}
            className={`${classes.timeSegment} ${activeField === FIELD_TYPES.MINUTE ? classes.activeSegment : ''}`}
            maxLength={MAX_INPUT_LENGTH}
            inputMode="numeric"
            aria-label="Minute"
          />
          <button
            type="button"
            ref={periodRef}
            onClick={handlePeriodClick}
            onFocus={handlePeriodFocus}
            onBlur={handlePeriodBlur}
            onKeyDown={(e) => handleKeyDown(e, FIELD_TYPES.PERIOD)}
            className={`${classes.periodButton} ${activeField === FIELD_TYPES.PERIOD ? classes.activeSegment : ''}`}
            aria-label="AM/PM"
          >
            {displayPeriod}
          </button>
        </div>
        {icons?.clock && (
          <div className={classes.clockIcon}>
            {icons.clock}
          </div>
        )}
      </div>
    </div>
  );
};

TimeTextField.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  parentApp: PropTypes.string,
  icons: PropTypes.object
};

TimeTextField.defaultProps = {
  label: '',
  parentApp: 'aet',
  icons: null
};

export default TimeTextField;
